import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Menu,
  Paper,
} from "@mui/material";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import deviceNoteStore from "../../../stores/DeviceNoteStore";
import authStore from "../../../stores/AuthStore";
import deviceStore from "../../../stores/DeviceStore";
import { useParams } from "react-router-dom";

const DeviceNote = observer(() => {
  const { pimios_id } = useParams();
  const actualDeviceId = deviceStore.devices.find(
    (d) => d.pimios_id === pimios_id?.split("-")[0]
  )?.id;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [noteData, setNoteData] = useState({
    title: "",
    content: "",
    importance: "normal",
    device_id: actualDeviceId,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Tab and menu state
  const [tab, setTab] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuNoteId, setMenuNoteId] = useState(null);

  useEffect(() => {
    deviceNoteStore.fetchNotes().catch((err) => {
      console.error("Error loading notes:", err);
      setSnackbar({
        open: true,
        message: "Failed to load notes",
        severity: "error",
      });
    });
    return () => deviceNoteStore.clearData();
  }, []);

  useEffect(() => {
    handleMenuClose();
  }, [tab]);

  const deviceNotes = deviceNoteStore.getNotesByDeviceId(actualDeviceId);
  const activeNotes = deviceNotes.filter((n) => !n.deleted_at);
  const archivedNotes = deviceNotes.filter((n) => n.deleted_at);

  const handleOpenModal = (note = null) => {
    if (note) {
      setNoteData({
        title: note.title,
        content: note.content,
        importance: note.importance || "normal",
        device_id: note.device_id,
      });
      setEditMode(true);
      setCurrentNoteId(note.id);
    } else {
      setNoteData({
        title: "",
        content: "",
        importance: "normal",
        device_id: actualDeviceId,
      });
      setEditMode(false);
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!actualDeviceId || !authStore.user?.id) {
      setSnackbar({
        open: true,
        message: "Not enough data",
        severity: "error",
      });
      return;
    }

    try {
      const payload = {
        ...noteData,
        device_id: actualDeviceId,
        created_by: authStore.user.id,
      };

      if (editMode) {
        await deviceNoteStore.updateNote(currentNoteId, payload);
      } else {
        await deviceNoteStore.addNote(payload);
      }

      setSnackbar({
        open: true,
        message: "Note saved successfully",
        severity: "success",
      });
      setIsModalOpen(false);
      setNoteData({
        title: "",
        content: "",
        importance: "normal",
        device_id: actualDeviceId,
      });
    } catch (error) {
      console.error("Error saving note:", error);
      setSnackbar({
        open: true,
        message: "Failed to save note",
        severity: "error",
      });
    }
  };

  const handleMenuOpen = (event, noteId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuNoteId(noteId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuNoteId(null);
  };

  const handleArchive = async () => {
    if (menuNoteId) {
      await deviceNoteStore.archiveNote(menuNoteId);
      setSnackbar({
        open: true,
        message: "Note archived",
        severity: "success",
      });
      handleMenuClose();
    }
  };

  const handlePermanentDelete = async () => {
    if (menuNoteId) {
      await deviceNoteStore.deleteNote(menuNoteId);
      setSnackbar({
        open: true,
        message: "Note permanently deleted",
        severity: "success",
      });
      handleMenuClose();
    }
  };

  const handleRevert = async (noteId) => {
    await deviceNoteStore.unarchiveNote(noteId);
    setSnackbar({
      open: true,
      message: "Note reverted to active",
      severity: "success",
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <DevicesOtherIcon
              fontSize="large"
              color="primary"
              sx={{ mr: 2, color: "#22C55E" }}
            />
            <Typography variant="h4" fontWeight="bold">
              Device Notes
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Notes for device ID: <strong>{pimios_id}</strong>
          </Typography>
        </CardContent>
      </Card>

      <Box className="flex justify-between items-center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
          onClick={() => handleOpenModal()}
          disabled={!actualDeviceId || !authStore.user?.id}
        >
          Add Note
        </Button>
        {!authStore.user?.id && (
          <Typography variant="caption" color="error">
            No authenticated user
          </Typography>
        )}
      </Box>

      {/* Tabs for Active/Archived */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={`Active Notes (${activeNotes.length})`}
            id="active-notes-tab"
            aria-controls="active-notes-panel"
          />
          <Tab
            label={`Archived Notes (${archivedNotes.length})`}
            id="archived-notes-tab"
            aria-controls="archived-notes-panel"
          />
        </Tabs>
      </Paper>

      {deviceNoteStore.loading && <Typography>Loading notes...</Typography>}
      {deviceNoteStore.error && (
        <Typography color="error">{deviceNoteStore.error}</Typography>
      )}

      {/* Active Notes Table */}
      {tab === 0 && !deviceNoteStore.loading && !deviceNoteStore.error && (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Importance</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeNotes.map((note) => (
              <TableRow key={note.id}>
                <TableCell>{note.id}</TableCell>
                <TableCell>{note.title}</TableCell>
                <TableCell>{note.content}</TableCell>
                <TableCell>{note.created_by || "Unknown"}</TableCell>
                <TableCell>{note.created_at?.slice(0, 10) || "N/A"}</TableCell>
                <TableCell>
                  <Chip
                    label={note.importance}
                    color={
                      note.importance === "high"
                        ? "error"
                        : note.importance === "normal"
                          ? "warning"
                          : "default"
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleOpenModal(note)}
                    sx={{ color: "#22C55E", mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, note.id)}
                    sx={{ color: "#6B7280" }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {activeNotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>No active notes found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Archived Notes Table */}
      {tab === 1 && !deviceNoteStore.loading && !deviceNoteStore.error && (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Archived At</TableCell>
              <TableCell>Importance</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {archivedNotes.map((note) => (
              <TableRow key={note.id}>
                <TableCell>{note.id}</TableCell>
                <TableCell>{note.title}</TableCell>
                <TableCell>{note.content}</TableCell>
                <TableCell>{note.created_by || "Unknown"}</TableCell>
                <TableCell>{note.created_at || "N/A"}</TableCell>
                <TableCell>{note.deleted_at || "N/A"}</TableCell>
                <TableCell>
                  <Chip
                    label={note.importance}
                    color={
                      note.importance === "high"
                        ? "error"
                        : note.importance === "normal"
                          ? "warning"
                          : "default"
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    onClick={() => handleRevert(note.id)}
                  >
                    Revert to Active
                  </Button>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, note.id)}
                    sx={{ color: "#6B7280", ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {archivedNotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>No archived notes found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Delete/Archive Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {tab === 0 && <MenuItem onClick={handleArchive}>Archive</MenuItem>}
        <MenuItem onClick={handlePermanentDelete}>Permanent Delete</MenuItem>
      </Menu>

      {/* Modal for Adding/Editing Device Note */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>{editMode ? "Edit Note" : "Add Note"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={noteData.title}
            onChange={(e) =>
              setNoteData({ ...noteData, title: e.target.value })
            }
          />
          <TextField
            label="Content"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            value={noteData.content}
            onChange={(e) =>
              setNoteData({ ...noteData, content: e.target.value })
            }
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="importance-label">Importance</InputLabel>
            <Select
              labelId="importance-label"
              value={noteData.importance}
              label="Importance"
              onChange={(e) =>
                setNoteData({ ...noteData, importance: e.target.value })
              }
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{
              textTransform: "uppercase",
              color: "#000",
              borderColor: "#000",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderColor: "#000",
              },
            }}
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              backgroundColor: "#22C55E",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#16A34A",
              },
            }}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default DeviceNote;
